import * as React from 'react'

import { InputGroup, InputGroupAddon, InputGroupInput } from './input-group'

const MAX_DECIMALS = 2

/** Keep digits plus the first decimal point, capping the fractional part to
 * cents. Stripping every non-digit (the previous behaviour) merged the cents
 * into the dollars and shifted the value — e.g. 20050.6 rendered as 200,506. */
function normalizeCurrency(value: string): { intPart: string; fracPart: string | null } {
  const cleaned = value.replace(/[^\d.]/g, '')
  const dot = cleaned.indexOf('.')
  if (dot === -1) {
    return { intPart: cleaned, fracPart: null }
  }
  return {
    intPart: cleaned.slice(0, dot),
    fracPart: cleaned
      .slice(dot + 1)
      .replace(/\./g, '')
      .slice(0, MAX_DECIMALS),
  }
}

function formatCurrency(value: string): string {
  const { intPart, fracPart } = normalizeCurrency(value)
  if (intPart === '' && fracPart === null) return ''
  const intFormatted = intPart === '' ? '0' : Number(intPart).toLocaleString('en-US')
  // A non-null fracPart means the user typed a ".", so keep it (even when empty)
  // so they can continue entering cents.
  return fracPart === null ? intFormatted : `${intFormatted}.${fracPart}`
}

function parseCurrency(value: string): number | null {
  const { intPart, fracPart } = normalizeCurrency(value)
  const normalized = fracPart === null ? intPart : `${intPart}.${fracPart}`
  if (normalized === '' || normalized === '.') return null
  const parsed = Number(normalized)
  return Number.isNaN(parsed) ? null : parsed
}

const isNegative = (text: string, allowNegative: boolean) => allowNegative && text.trim().startsWith('-')

function parseAmount(text: string, allowNegative: boolean): number | null {
  const amount = parseCurrency(text)
  return amount !== null && isNegative(text, allowNegative) ? -amount : amount
}

/** Format a committed numeric value for display: group thousands and, when the
 * value has cents, always show two decimal places (e.g. 19905.6 -> "19,905.60").
 * Whole-dollar values render without a decimal part. */
function formatValue(value: number): string {
  const hasCents = !Number.isInteger(value)
  return value.toLocaleString('en-US', {
    minimumFractionDigits: hasCents ? MAX_DECIMALS : 0,
    maximumFractionDigits: MAX_DECIMALS,
  })
}

interface CurrencyInputProps {
  value: number | null | undefined
  onChange: (value: number | null) => void
  'aria-invalid'?: boolean
  placeholder?: string
  id?: string
  /** Keeps a leading minus, for an amount that can go either way, such as a change in premium. */
  allowNegative?: boolean
  disabled?: boolean
}

export function CurrencyInput({
  value,
  onChange,
  placeholder,
  id,
  allowNegative = false,
  disabled,
  ...props
}: CurrencyInputProps) {
  const [display, setDisplay] = React.useState(() => (value != null ? formatValue(value) : ''))

  React.useEffect(() => {
    setDisplay((prev) => {
      // Don't reformat over in-progress typing (e.g. a trailing "." or ".0")
      // when the number being entered already matches the incoming value.
      if (parseAmount(prev, allowNegative) === value) return prev
      return value != null ? formatValue(value) : ''
    })
  }, [value, allowNegative])

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    const raw = e.target.value
    setDisplay(`${isNegative(raw, allowNegative) ? '-' : ''}${formatCurrency(raw)}`)
    onChange(parseAmount(raw, allowNegative))
  }

  return (
    <InputGroup>
      <InputGroupAddon align="inline-start">$</InputGroupAddon>
      <InputGroupInput
        id={id}
        value={display}
        onChange={handleChange}
        placeholder={placeholder}
        inputMode="decimal"
        disabled={disabled}
        aria-invalid={props['aria-invalid']}
      />
    </InputGroup>
  )
}
